'use client';
// @spec authentication authentication-google-oauth
import { isUsableUserId } from '@fpo3/famfam-core/user-scoped-storage-keys';
import { useSession } from 'next-auth/react';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';

interface AuthUser {
  name: string | null;
  email: string | null;
  image: string | null;
  id: string;
  // FPO-723: surfaced from NextAuth Session.user.familyId so client-side
  // observability (Datadog RUM) can tag sessions without an extra fetch.
  // Optional: pre-existing JWTs (signed before this feature shipped) won't
  // carry the field until the JWT-callback hydration fallback runs.
  familyId?: string;
}

export interface AuthState {
  user: AuthUser | null;
  status: 'authenticated' | 'loading' | 'unauthenticated';
}

/**
 * Exported so tests and storybook shims can supply an `AuthState` directly
 * without rendering `SessionProvider`. Production code should use
 * `<AuthProvider>` and consume via `useAuth()`.
 */
export const AuthContext = createContext<AuthState>({
  user: null,
  status: 'loading',
});

/**
 * Hook to get auth state from the AuthProvider context.
 * Use this instead of useSession() from next-auth/react.
 */
export function useAuth(): AuthState {
  return useContext(AuthContext);
}

/**
 * Convenience hook that returns the authenticated user's id or `null` when
 * auth is loading or the user is signed out. Collapses the common
 * `const { user } = useAuth(); const userId = user?.id ?? null;` prelude into
 * a single call and gives every user-scoped storage / caching call site one
 * choke point for the empty-id guard.
 */
export function useAuthUserId(): string | null {
  return useContext(AuthContext).user?.id ?? null;
}

interface AuthProviderProps {
  children: ReactNode;
  isMockMode: boolean;
}

/**
 * Auth provider that wraps NextAuth in real mode and provides
 * a static mock session in mock mode. Components use useAuth()
 * instead of useSession() to abstract away the auth source.
 */
export function AuthProvider({ children, isMockMode }: AuthProviderProps) {
  if (isMockMode) {
    return <MockAuthProvider>{children}</MockAuthProvider>;
  }
  return <RealAuthProvider>{children}</RealAuthProvider>;
}

function RealAuthProvider({ children }: { children: ReactNode }) {
  const { data: session, status } = useSession();
  const authState = useMemo<AuthState>(() => {
    const sessionUser = session?.user;
    const rawId = sessionUser != null ? (sessionUser as { id?: unknown }).id : undefined;
    const rawFamilyId =
      sessionUser != null ? (sessionUser as { familyId?: unknown }).familyId : undefined;
    const familyId =
      typeof rawFamilyId === 'string' && rawFamilyId !== '' ? rawFamilyId : undefined;
    // Gate the user id through the same predicate that user-scoped storage
    // uses, so the provider can't hand consumers an id that would be rejected
    // when composing a key (empty, whitespace-padded, colon-bearing).
    const userId = typeof rawId === 'string' && isUsableUserId(rawId) ? rawId : null;
    // A session without a usable user.id is treated as terminally
    // unauthenticated — user-scoped browser storage (localStorage, Cache API)
    // is keyed by this id, and falling back to an empty string would
    // collapse every user into a shared namespace. Returning `loading` here
    // would also be wrong: the underlying NextAuth status has already settled
    // (`authenticated`), so consumers waiting on auth resolution would hang
    // forever. Downgrade to a terminal state instead.
    if (sessionUser == null || userId == null) {
      return {
        user: null,
        status: status === 'authenticated' ? 'unauthenticated' : status,
      };
    }
    return {
      user: {
        name: sessionUser.name ?? null,
        email: sessionUser.email ?? null,
        image: sessionUser.image ?? null,
        id: userId,
        ...(familyId != null && { familyId }),
      },
      status,
    };
  }, [session, status]);

  return <AuthContext.Provider value={authState}>{children}</AuthContext.Provider>;
}

function MockAuthProvider({ children }: { children: ReactNode }) {
  const [mockSession, setMockSession] = useState<AuthState>({
    user: null,
    status: 'loading',
  });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { MOCK_SESSION } = await import('@fpo3/famfam-core/mock-fixtures');
        if (cancelled) return;
        setMockSession({
          user: {
            name: MOCK_SESSION.user.name,
            email: MOCK_SESSION.user.email,
            image: MOCK_SESSION.user.image ?? null,
            id: MOCK_SESSION.user.id,
            familyId: MOCK_SESSION.user.familyId,
          },
          status: 'authenticated',
        });
      } catch {
        // Mock-mode is dev-only; if the dynamic import fails the dev server
        // will surface the underlying error elsewhere. Do not crash the
        // provider — leave mockSession in its initial loading state.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return <AuthContext.Provider value={mockSession}>{children}</AuthContext.Provider>;
}
